/**
 * Runtime props store.
 *
 * A module-level `Map<componentId, unknown>` that captures the most
 * recent props passed to each registered component. Used by the admin
 * config page to power the live preview: when an admin CMD/Ctrl+clicks a
 * component, the config page renders the component using the exact
 * props that were on the calling page at click time.
 *
 * Characteristics:
 *
 * - **Per-class, last-write-wins.** Multiple instances of the same
 *   component share a single slot — the last render wins. Per-instance
 *   capture is out of scope for the MVP (see spec "Runtime props capture
 *   for the live preview" caveats).
 *
 * - **Live references, not serialized.** Props are stored as-is, so
 *   callbacks and React-element children remain usable in the live
 *   preview. Same-session navigation preserves them; a hard refresh
 *   resets the store to empty and the config page falls back to
 *   `ComponentRegistration.previewProps`.
 *
 * - **In-memory only.** Never touches `localStorage` — we don't want to
 *   serialize random prop graphs, and the registry fallback handles the
 *   cold-start case cleanly.
 *
 * - **No React imports.** Writes happen from the `withComponentRules`
 *   HOC (Phase 6) on every render; reads happen from the config page.
 *   Keeping this file React-free makes it trivially reusable from unit
 *   tests without a React render tree.
 */

// ---------------------------------------------------------------------------
// Internal storage
// ---------------------------------------------------------------------------

const runtimePropsStore = new Map<string, unknown>();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Record the latest props seen for a component id.
 *
 * The HOC calls this on every render — it is intentionally cheap and
 * does no equality check. Callers that care about re-render cost should
 * memoise at the props level before reaching the HOC.
 *
 * @param id    - The component id (matches `ComponentRegistration.id`).
 * @param props - The exact props object last rendered. Kept by reference.
 */
export function setRuntimeProps(id: string, props: unknown): void {
  runtimePropsStore.set(id, props);
}

/**
 * Look up the most recently captured props for a component id.
 *
 * Returns `undefined` when no component of that id has rendered in the
 * current session, or after a hard refresh — the config page uses this
 * signal to fall back to `ComponentRegistration.previewProps`.
 */
export function getRuntimeProps(id: string): unknown {
  return runtimePropsStore.get(id);
}

/**
 * Clear runtime props.
 *
 * - `clearRuntimeProps()` — clears every captured entry (test-only /
 *   teardown).
 * - `clearRuntimeProps("<id>")` — clears a single slot. Used by the
 *   config page after a successful navigation away, or by tests that
 *   want to isolate a single entry.
 */
export function clearRuntimeProps(id?: string): void {
  if (id === undefined) {
    runtimePropsStore.clear();
    return;
  }
  runtimePropsStore.delete(id);
}
